import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getMyLatestMom, confirmMom, addMomPoints } from '../../services/momsService';
//import '../../assets/css/Moms-section_style/MOM_Confirmation.css';

export default function MomConfirmation({ visible = true, onDone = () => {} }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mom, setMom] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [points, setPoints] = useState([{ point_serial_no: 1, point_description: '' }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const rec = await getMyLatestMom();
        if (!mounted) return;

        // if no pending MOM -> inform parent to continue
        if (!rec || !rec.moms_meeting_id) {
          onDone();
          return;
        }

        setMom(rec);
      } catch (err) {
        console.error(err);
        //toast.error('Failed to load MOM confirmation');
        onDone();
      } finally {
        setLoading(false);
      }
    }

    if (visible) {
      document.body.style.overflow = 'hidden';
      load();
    }

    return () => {
      mounted = false;
      document.body.style.overflow = '';
    };
  }, [visible, onDone]);

  useEffect(() => {
    // reset points when showing missing section
    setPoints([{ point_serial_no: 1, point_description: '' }]);
  }, [showMissing]);

  if (!visible || loading || !mom) return null;

  const id = mom.moms_meeting_id ?? mom.id;

  function addPoint() {
    setPoints((prev) => [...prev, { point_serial_no: prev.length + 1, point_description: '' }]);
  }

  function updatePoint(idx, val) {
    setPoints((prev) => prev.map((p, i) => (i === idx ? { ...p, point_description: val } : p)));
  }

  function removePoint(idx) {
    setPoints((prev) => {
      const nxt = prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, point_serial_no: i + 1 }));
      return nxt.length ? nxt : [{ point_serial_no: 1, point_description: '' }];
    });
  }

  function validatePoints() {
    for (const p of points) {
      if (!p.point_description || !p.point_description.trim()) {
        toast.error('All missing points are mandatory');
        return false;
      }
      if (p.point_description.length > 100) {
        toast.error('Each point must be at most 100 characters');
        return false;
      }
    }
    return true;
  }

  async function handleConfirm() {
    try {
      setSubmitting(true);
      const payload = {
        moms_meeting_id: id,
        partner_id: user?.id ?? 1,
        created_by: user?.id ?? 1,
        is_confirmed: true,
        confirmed_date: new Date().toISOString(),
      };
      await confirmMom(payload);
      toast.success('MOM confirmed');
      onDone();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Confirm failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitMissing() {
    if (!validatePoints()) return;
    try {
      setSubmitting(true);
      const payload = {
          points: points.map((p) => ({
            point_serial_no: p.point_serial_no,
            point_description: p.point_description
          })),
          //points: points.map(p => p.point_description),
          created_by: user?.id ?? 1
        };
      //const payload = points.map((p) => ({ point_serial_no: p.point_serial_no, point_description: p.point_description,created_by: user?.id ?? 1 }));
      await addMomPoints(id, payload);
      toast.success('Missing points submitted. Partners notified.');
      onDone();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="mom-confirmation-wrapper"
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="mom-confirmation-card" style={{ maxWidth: 980, width: '100%', background: '#fff', borderRadius: 8, padding: 18 }}>
        <h1 className="mom-title">MOM Confirmation</h1>
        <hr />

        <div className="mom-table-container" style={{ backgroundColor: '#f0f4f8', padding: 15, borderRadius: 6 }}>
          <table className="mom-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>MOM Confirmation</th>
                <th>Meeting ID</th>
                <th>Meeting Date</th>
                <th>Meeting Time</th>
                <th>Meeting Type</th>
                <th>Meeting Mode</th>
                <th>Venue</th>
                <th>Attendees</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td></td>
                <td style={{ color: '#1E3573', fontWeight: 500 }}>{mom.meeting_code ?? id}</td>
                <td>{(mom.meeting_date || '').slice(0, 10).split('-').reverse().join('/')}</td>
                <td>{mom.meeting_time ?? ''}</td>
                <td>{mom.meeting_type ?? ''}</td>
                <td>{mom.meeting_mode ?? ''}</td>
                <td>{mom.venue ?? ''}</td>
                <td className="attendees-cell">
                  {(mom.attendees || []).map((a, i) => (
                    <div key={i} className="attendee">
                      {a.name ?? a.full_name ?? a.partner_name ?? String(a)}
                    </div>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderTop: 'none', padding: 15, marginTop: 12 }}>
            <div className="points-section" style={{ marginTop: 0 }}>
              <h3 style={{ fontWeight: 500, marginBottom: 5 }}>Points Discussed</h3>
              <div style={{ paddingLeft: 20 }}>
                {(mom.points || []).map((p, i) => (
                  <div key={i} style={{ marginBottom: 5 }}>
                    {p.point_serial_no ?? i + 1}. {p.point_description ?? p.description ?? ''}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons" style={{ marginTop: 15, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {!showMissing && (
            <button
              className="point-missing-button"
              onClick={() => setShowMissing(true)}
              disabled={submitting}
              style={{ backgroundColor: '#1F3161', color: 'white', border: 'none', borderRadius: 4, padding: '8px 12px', fontWeight: 500 }}
            >
              Point Missing
            </button>
          )}
          <button
            className="confirm-button"
            onClick={handleConfirm}
            disabled={submitting}
            style={{ backgroundColor: '#1F3161', color: 'white', border: 'none', borderRadius: 4, padding: '8px 12px', fontWeight: 500 }}
          >
            Confirm
          </button>
        </div>

        {showMissing && (
          <div className="missing-points-entry-card" style={{ backgroundColor: '#f0f4f8', borderRadius: 6, padding: 15, marginTop: 20 }}>
            <h3 style={{ marginTop: 0, fontWeight: 500, color: '#333' }}>Missing Points Entry</h3>
            <table className="missing-points-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #e5e7eb', background: '#f8fafb', padding: 10, textAlign: 'left', fontWeight: 500 }}>Meeting ID</th>
                  <th style={{ border: '1px solid #e5e7eb', background: '#f8fafb', padding: 10, textAlign: 'left', fontWeight: 500 }}>Missing Points</th>
                  <th style={{ border: '1px solid #e5e7eb', background: '#f8fafb', padding: 10 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #e5e7eb', padding: 10, color: '#1E3573', fontWeight: 500,width:120 }}>{mom.meeting_code ?? id}</td>
                  <td style={{ border: '1px solid #e5e7eb', padding: 10, color: '#666' }}>
                    {points.map((p, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ width: 36, color: '#1E3573' }}>{p.point_serial_no}.</div>
                        <input
                          type="text"
                          value={p.point_description}
                          onChange={(e) => updatePoint(idx, e.target.value)}
                          placeholder="Enter missing point"
                          maxLength={100}
                          required
                          style={{ flex: 1, padding: 8 }}
                        />
                        {points.length > 1 && (
                          <button type="button" className="modify-button" onClick={() => removePoint(idx)}>
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: 10, verticalAlign: 'top', width:150 }}>
                      <div>
                        <button onClick={addPoint} style={{ backgroundColor: '#1F3161', color: 'white', border: 'none', borderRadius: 4, padding: '8px 12px', marginBottom: 8 }}>
                        Add Point +
                        </button>
                        <button onClick={() => setShowMissing(false)} style={{ marginRight: 8 }}>
                          Cancel
                        </button>
                        <button onClick={handleSubmitMissing} disabled={submitting} style={{ backgroundColor: '#1F3161', color: 'white', border: 'none', borderRadius: 4, padding: '8px 12px' }}>
                          Submit
                        </button>
                      </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
